import type { CompensationLineItem } from '../types';

/**
 * MED-695: Display order for 1:1 session-type sub-rows under the grouped
 * "1:1 Coaching" breakdown section. Matches the backend one_on_one_sda enum
 * (constants/compensation.ts ONE_ON_ONE_SDA_TYPES).
 */
export const ONE_ON_ONE_SESSION_TYPE_ORDER = [
  'standard_1_1',
  'two_on_one',
  '360_debrief',
  'hogan_debrief',
  'other_assessment_debrief',
] as const;
export type OneOnOneSessionType = (typeof ONE_ON_ONE_SESSION_TYPE_ORDER)[number];

/** True when this line item is a per-session-type 1:1 row (produced by the
 * calc engine grouping by session_type). */
export function isOneOnOneLineItem(li: CompensationLineItem): boolean {
  return li.item_key === 'one_on_one' || li.item_key === 'two_on_one';
}

/** True when this line item is the cohort-wide Coach Debrief flat fee that
 * accompanies a 360 Debrief. Pre-MED-695 this rendered as a standalone
 * "360 Debrief" row; post-MED-695 it nests under the 360_debrief 1:1 row. */
export function isCoachDebriefFlatFee(li: CompensationLineItem): boolean {
  return li.item_key === 'coach_debrief';
}

function getSessionType(li: CompensationLineItem): OneOnOneSessionType | null {
  const raw = (li.metadata as { session_type?: unknown })?.session_type;
  if (typeof raw !== 'string') return null;
  return ONE_ON_ONE_SESSION_TYPE_ORDER.includes(raw as OneOnOneSessionType)
    ? (raw as OneOnOneSessionType)
    : null;
}

export interface OneOnOneGroupRow {
  sessionType: OneOnOneSessionType;
  lineItem: CompensationLineItem;
  /** Only populated on the 360_debrief row. */
  nestedCoachDebrief: CompensationLineItem | null;
}

export interface GroupedBreakdown {
  /** Per-session-type 1:1 rows in enum order, only types with sessions present. */
  oneOnOneGroups: OneOnOneGroupRow[];
  /** Non 1:1 / non coach-debrief line items, in their original order. */
  otherLineItems: CompensationLineItem[];
  /** True when at least one 1:1 row exists. When false, no "1:1 Coaching" section renders. */
  hasOneOnOneSection: boolean;
}

/**
 * Split line_items into the per-type "1:1 Coaching" grouping plus the rest
 * (group sessions, flat rates, workshop, in-person, etc). The Coach Debrief
 * flat fee is attached to the 360_debrief row for nested display.
 *
 * Input invariant (from backend CompensationCalculationService MED-694 AC6a):
 *   - Each per-type 1:1 line item carries metadata.session_type.
 *   - When any 1:1 session is a 360_debrief, a separate item_key='coach_debrief'
 *     line item is appended.
 */
export function groupBreakdownBySessionType(
  lineItems: CompensationLineItem[],
): GroupedBreakdown {
  const oneOnOneByType = new Map<OneOnOneSessionType, CompensationLineItem>();
  const other: CompensationLineItem[] = [];
  let coachDebrief: CompensationLineItem | null = null;

  for (const li of lineItems) {
    if (isOneOnOneLineItem(li)) {
      const sessionType = getSessionType(li);
      if (sessionType) {
        oneOnOneByType.set(sessionType, li);
        continue;
      }
      // Legacy row without metadata.session_type — keep flat.
      other.push(li);
      continue;
    }
    if (isCoachDebriefFlatFee(li)) {
      coachDebrief = li;
      continue;
    }
    other.push(li);
  }

  const oneOnOneGroups: OneOnOneGroupRow[] = [];
  for (const sessionType of ONE_ON_ONE_SESSION_TYPE_ORDER) {
    const lineItem = oneOnOneByType.get(sessionType);
    if (!lineItem) continue;
    oneOnOneGroups.push({
      sessionType,
      lineItem,
      nestedCoachDebrief: sessionType === '360_debrief' ? coachDebrief : null,
    });
  }

  // Fallback: if a coach_debrief fee exists but no 360_debrief 1:1 row, keep
  // the fee visible as a standalone row rather than hiding it.
  if (coachDebrief && !oneOnOneByType.has('360_debrief')) {
    other.push(coachDebrief);
  }

  return {
    oneOnOneGroups,
    otherLineItems: other,
    hasOneOnOneSection: oneOnOneGroups.length > 0,
  };
}

/**
 * Sum the "1:1 Coaching" subtotal (per-type rows + nested coach debrief),
 * honoring pending and existing adjustments via the caller-supplied resolver.
 * Items flagged `!is_assigned_to_coach` (MED-677 post-announcement) are
 * excluded when `excludeUnassigned` is true.
 */
export function sumOneOnOneSubtotalCents(
  groups: OneOnOneGroupRow[],
  getEffectiveTotal: (li: CompensationLineItem) => number,
  options: { excludeUnassigned?: boolean } = {},
): number {
  let total = 0;
  for (const g of groups) {
    if (options.excludeUnassigned && g.lineItem.is_assigned_to_coach === false) continue;
    total += getEffectiveTotal(g.lineItem);
    if (g.nestedCoachDebrief) {
      if (options.excludeUnassigned && g.nestedCoachDebrief.is_assigned_to_coach === false) continue;
      total += getEffectiveTotal(g.nestedCoachDebrief);
    }
  }
  return total;
}
